import React from "react";
import { Button, Input } from "@/components/atoms";
import type { InvestorComment, InvestorCommentReply } from "@/types/startup.types";

const COMMENT_SECTIONS = [
  "Financial Snapshot",
  "Revenue Breakdown",
  "Expense Breakdown",
  "Cash Flow Chart",
  "Break-Even Analysis",
  "Customer List",
  "Team",
  "Events & Milestones",
  "Assets",
] as const;

type InvestorCommentsSectionProps = {
  comments: InvestorComment[];
  commentSection: string;
  commentText: string;
  commentError: string | null;
  onSectionChange: (section: string) => void;
  onTextChange: (text: string) => void;
  onSubmit: () => void;
};

export const InvestorCommentsSection: React.FC<InvestorCommentsSectionProps> = ({
  comments,
  commentSection,
  commentText,
  commentError,
  onSectionChange,
  onTextChange,
  onSubmit,
}) => (
  <section className="rounded-[20px] border border-white/10 bg-surface p-4 sm:p-5">
    <h2 className="text-lg font-bold">Comments</h2>
    <p className="mt-1 text-sm text-white/55">
      Leave feedback on a section. Other viewers on this link can see your comments.
    </p>
    <div className="mt-4 flex flex-col gap-3 sm:grid sm:grid-cols-[200px_1fr_auto]">
      <select
        className="sh-select w-full"
        value={commentSection}
        onChange={(e) => onSectionChange(e.target.value)}
      >
        {COMMENT_SECTIONS.map((section) => (
          <option key={section} value={section}>
            {section}
          </option>
        ))}
      </select>
      <Input
        value={commentText}
        onChange={(e) => onTextChange(e.target.value)}
        placeholder="Share a question or note..."
      />
      <Button
        type="button"
        variant="primary"
        size="md"
        className="w-full sm:w-auto"
        disabled={!commentText.trim()}
        onClick={onSubmit}
      >
        Post
      </Button>
    </div>
    {commentError ? <div className="mt-2 text-sm text-danger">{commentError}</div> : null}
    <div className="mt-5 space-y-3">
      {comments.map((comment) => (
        <div
          key={comment.id}
          className="rounded-[14px] border border-white/10 bg-surface-elevated/40 p-3"
        >
          <div className="text-xs text-white/50">
            <span className="font-semibold text-white/75">{comment.displayName}</span> ·{" "}
            {comment.section} · {new Date(comment.createdAt).toLocaleString()}
          </div>
          <p className="mt-2 text-sm text-white/80">{comment.text}</p>
          {comment.replies?.map((reply: InvestorCommentReply, index: number) => (
            <p
              key={index}
              className="mt-2 border-l border-white/10 pl-3 text-sm text-white/70"
            >
              <span className="font-semibold">{reply.authorName}:</span> {reply.text}
            </p>
          ))}
        </div>
      ))}
    </div>
  </section>
);
